import Bitmap = createjs.Bitmap;
import DisplayObject = createjs.DisplayObject;

export default class Utils {
  public static WIDTH: number;
  public static HEIGHT: number;
  public static SCREEN_WIDTH: number;
  public static SCREEN_HEIGHT: number;
  public static SCREEN_RATIO: number;


  public static init(canvas: HTMLCanvasElement): void {
    Utils.WIDTH = 1920;
    Utils.HEIGHT = Utils.WIDTH * (canvas.height / canvas.width);
    Utils.SCREEN_WIDTH = screen.width;
    Utils.SCREEN_HEIGHT = screen.height;
    Utils.SCREEN_RATIO = Utils.SCREEN_HEIGHT/ Utils.SCREEN_WIDTH;
  }

  public static scaleDownX(bitmap: Bitmap, fitX: number): void {
    let scale: number = Math.min(1, fitX / bitmap.image.width);

    bitmap.scaleX = scale;
    bitmap.scaleY = scale;
  }

  /**
   * Scale down a bitmap so it fits the given width/height, keeping the aspect ratio.
   * @param {Bitmap} bitmap
   * @param {number} fitX
   * @param {number} fitY
   */
  public static scaleDownXY(bitmap: Bitmap, fitX: number, fitY: number): void {
    let scaleX: number = Math.min(1, fitX / bitmap.image.width);
    let scaleY: number = Math.min(1, fitY / bitmap.image.height);
    let scale: number = Math.min(scaleX, scaleY);

    bitmap.scaleX = scale;
    bitmap.scaleY = scale;
  }

  public static centerInContainer(
    object: DisplayObject,
    container: DisplayObject
  ): void {
    let innerBounds: createjs.Rectangle = object.getTransformedBounds();
    let outerBounds: createjs.Rectangle  = container.getTransformedBounds();
    object.x = outerBounds.width - innerBounds.width / 2;
    object.y = outerBounds.height - innerBounds.height / 2;
  }

  /**
   * Scale up an image so it covers the given dimensions.
   * Re-centers the bitmap so the center of the bitmap is at the screen center.
   *
   * @param bitmap
   * @param minX
   * @param minY
   */
  public static scaleUpXY(bitmap: Bitmap, minX: number, minY: number): void {
    let scaleX: number = Math.max(1, minX / bitmap.image.width);
    let scaleY: number = Math.max(1, minY / bitmap.image.height);
    let scale: number = Math.max(scaleX, scaleY);

    bitmap.scaleX = scale;
    bitmap.scaleY = scale;
    bitmap.x = -(bitmap.image.width * scale - Utils.WIDTH) / 2;
    bitmap.y = -(bitmap.image.height * scale - Utils.HEIGHT) / 2;
  }

  public static scaleToFit(bitmap: Bitmap, maxX: number, maxY: number): void {
    let scale: number = Math.min(maxX / bitmap.image.width, maxY / bitmap.image.height);

    bitmap.scaleX = scale;
    bitmap.scaleY = scale;
    bitmap.x = -(bitmap.image.width * scale - Utils.WIDTH) / 2;
    bitmap.y = -(bitmap.image.height * scale - Utils.HEIGHT) / 2;
  }

  // http://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
  // translated to typescript
  public static getParameterByName(name: string): string {
    let url: string = window.location.href;
    name = name.replace(/[\[\]]/g, "\\$&");
    let regex: RegExp = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    let results: RegExpExecArray = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return "";
    }
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  /**
   * Utility for calling a function with a delay that can be zero, in which case
   * it should directly be called (instead of being deferred by setTimeout).
   *
   * @param f
   * @param delay
   * @returns {any}
   */
  public static runWithTimeout(f: () => void, delay: number): any {
    if (delay > 0) {
      return setTimeout(f, delay);
    } else {
      f();

      return null;
    }
  }

  public static initAudio(): boolean {
    console.log("My protocol is " + window.location.protocol);

    if (/file/.test(window.location.protocol)) {
      // android; don't use webaudio (needs xhr loading)
      console.log("soundjs: fallback to html audio plugin");

      return createjs.Sound.registerPlugins([createjs.HTMLAudioPlugin]);
    } else {
      console.log("soundjs: using webaudio with htmlaudio fallback");
      return createjs.Sound.initializeDefaultPlugins();
    }
  }

  public static centerBitmap(bitmap: createjs.Bitmap): void {
    bitmap.regX = bitmap.image.width / 2;
    bitmap.regY = bitmap.image.height / 2;
  }

  public static removeDuplicatesFromManifest(manifest: Array<any>): Array<any> {
    // need to remove duplicates; preloadjs has a known bug with duplicate audio files
    // https://github.com/CreateJS/PreloadJS/issues/210
    return manifest.filter(function(item: any, index: number, inputArray: Array<any>): boolean {
      var src: string = item.src;
      for (let i: number = index + 1; i < inputArray.length; i++) {
        if (inputArray[i].src === src) {
          if (inputArray[i].id !== item.id) {
            throw "Cannot remove duplicates with different ids: " +
              inputArray[i].id +
              " and " +
              item.id;
          }
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Get the adjusted y value.
   *
   * @param {number} y the y value, assuming a 1080-height canvas
   */
  public static getY(y: number): number {
    let ourHeight: number = Utils.HEIGHT;
    let assumedHeight: number = 1080;
    return y * (ourHeight / assumedHeight);
  }

  /**
   * Place an image at the specified x,y coordinates,
   * and set the registration point of the bitmap to it's center.
   * @param {Bitmap} bmp
   * @param {number} x
   * @param {number} y
   */
  public static placeAtCenter(bmp: Bitmap, x: number, y: number): void {
    bmp.regX = bmp.image.width / 2;
    bmp.regY = bmp.image.height / 2;
    bmp.x = x + bmp.regX;
    bmp.y = y + bmp.regY;
  }

  public static randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  /**
   * Make the entire contents of the bitmap clickable by setting a hit area on the object.
   * @param {createjs.Bitmap} bmp
   */
  public static makeClickable(bmp: Bitmap): void {
    let hitArea: createjs.Shape = new createjs.Shape();
    hitArea.graphics
      .beginFill("#000")
      .drawRect(0, 0, bmp.image.width, bmp.image.height);
    bmp.hitArea = hitArea;
  }

  public static getUrlForFile(filename: string): string {
    // convert into absolute url
    let link: HTMLAnchorElement = document.createElement("a");
    link.href = filename;
    // href is magically converted into an absolute url
    return link.href;
  }

  /**
   * Randomizes a URL so the browser won't cache it.
   * Useful for files that can change on disk, ie. recordings / photos.
   *
   * @param url
   */
  public static randomizeUrl(url: string): string {
    return url + "?" + Math.random() * 100000;
  }
}



// WEBPACK FOOTER //
// ../lib/src/utils.ts